<template>
  <el-dialog
    title="選擇標籤"
    :visible.sync="observeVisible"
    width="610px"
    :close-on-click-modal="false"
    @close="$emit('close')"
    @closed="onClosed"
    @open="onOpen"
  >
    <div v-loading="isLoading">
      <!-- ANCHOR 搜尋標籤 -->
      <div class="mb-[12px] grid grid-cols-2 gap-[12px]">
        <BaseElInput
          v-model="nameSearch"
          class="!w-full"
          suffix-icon="el-icon-search"
          placeholder="請輸入標籤名稱搜尋"
          clearable
        />
        <BaseDataSelect
          :value.sync="groupSearch"
          class="!w-full"
          placeholder="請選擇標籤群組"
          clearable
          :data-list="groupOptions"
          label-key="label"
          obj-key="value"
        />
      </div>
      <!-- 插入 -->
      <slot name="after-filter" />
      <!-- ANCHOR 新增標籤 -->
      <div v-if="enableCreate">
        <div v-if="!toggleCreate" class="create-tag-toggle">
          <BaseElButton class="btn-toggle-create" @click="toggleCreate = true">
            建立新標籤
            <i class="el-icon-plus" />
          </BaseElButton>
        </div>
        <div v-if="toggleCreate" class="flex">
          <BaseElForm
            ref="form"
            class="w-full"
            :model="formData"
            :rules="formRules"
            @submit.native.prevent
          >
            <BaseElFormItem prop="name">
              <div class="flex items-center justify-between" style="gap: 12px">
                <BaseElInput v-model.trim="formData.name" block class="create-tag" placeholder="請輸入標籤名稱" />
                <p class="btn confirm" @click="onCreateTag">確認</p>
                <p class="btn cancel" @click="toggleCreate = false">取消</p>
              </div>
            </BaseElFormItem>
          </BaseElForm>
        </div>
      </div>
      <!-- ANCHOR 選擇標籤 -->
      <div class="wrapper">
        <div class="selected-wrapper">
          <Tag
            v-for="tag in selected"
            :key="tag"
            type="action"
            closable
            @close="removeSelect(tag)"
          >
            <span class="selected-tag">
              {{ findSelectTag(tag) }}
            </span>
          </Tag>
        </div>
        <div class="list-wrapper">
          <div v-if="showSelectAll" class="select-all-wrapper">
            <BaseElCheckbox
              :value="isAllSelected"
              :indeterminate="isIndeterminate"
              @change="toggleSelectAll"
            >
              全選
            </BaseElCheckbox>
          </div>
          <BaseElCheckboxGroup v-model="selected" class="item-group">
            <template v-for="(item, index) in showTagList[tableOptions.page - 1]">
              <BaseElCheckbox
                v-if="item.type === 'option'"
                :key="index"
                :label="item.option.id"
                :disabled="disabledTag(item.option)"
              >
                <p class="truncate" style="width: 220px;">{{ item.option.name }}</p>
              </BaseElCheckbox>
              <div v-else :key="'label' + index" class="border-b border-primary-100 mr-[30px]">
                <span class="inline text-[14px] text-primary-100 font-medium">
                  {{ item.label }}
                </span>
              </div>
            </template>
          </BaseElCheckboxGroup>
          <Pagination
            class="pagination"
            :curPage.sync="tableOptions.page"
            :pageLimit="tableOptions.pageLimit"
            :total="mixedGroupLabelTags.length"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-end items-center" style="padding-top: 40px">
      <BaseElButton plain @click="observeVisible = false">取消</BaseElButton>
      <BaseElButton type="primary" @click="onSubmit">確認</BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { get, find, chunk } from 'lodash'
import { useAsyncState, useVModel } from '@vueuse/core'
import { uiGroupTagTypeName } from '@/config/member'
import { useTagForm } from '@/views/Marketing/components/useTagForm'
import { useTagStore } from '@/views/Marketing/components/useTagStore'
import { defineComponent, getCurrentInstance, ref } from 'vue'
import BaseDataSelect from './BaseDataSelect.vue'
import { useTagGroup } from '@/use/useTagGroup'

const uiGroupTagTypeName_ = {
  _custom: '自定義',
  ...uiGroupTagTypeName,
  _other: '其他',
}

export default defineComponent({
  name: 'MemberTagAdvanceSelect',
  components: {
    BaseDataSelect,
  },
  props: {
    modelValue: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    show: { type: Boolean, default: false },
    max: {
      type: Number,
      default: 0,
    },
    enableCreate: { type: Boolean, default: false },
    includeSystem: { type: Boolean, default: true },
    excludedTagIds: { type: Array, default: () => [] },
    disableGroupedTags: { type: Boolean, default: false },
  },
  emits: ['update:show', 'update:modelValue'],
  setup (props, { emit }) {
    const observeVisible = useVModel(props, 'show', emit)

    const {
      form,
      formData,
      formRules,
      resetForm,
      submitForm,

    } = useTagForm({})

    const toggleCreate = ref(false)

    const onCreateTag = async () => {
      const valid = await submitForm()
      if (!valid) return
      const [, err] = await req.execute(0, { ...formData })
      if (err) return $message.error(err)

      resetForm()
      toggleCreate.value = false
    }

    const { $message } = getCurrentInstance().proxy
    const {
      createTag,
    } = useTagStore()

    const req = useAsyncState(createTag, {}, { immediate: false })

    const { tagGroups: customGroups, fetchTagGroups: fetchCustomGroups } = useTagGroup()

    return {
      observeVisible,
      form,
      formData,
      formRules,
      resetForm,
      onCreateTag,
      isLoading: req.isLoading,
      toggleCreate,
      customGroups,
      fetchCustomGroups,
    }
  },
  data: () => ({
    nameSearch: '',
    groupSearch: '',

    selected: [],

    tableOptions: {
      page: 1,
      pageLimit: 16,
    },
  }),
  computed: {
    // tagId → custom group objects lookup (一標籤可屬多群組)
    tagToCustomGroups () {
      const map = {}
      for (const group of this.customGroups) {
        for (const tag of (group.MTags || [])) {
          if (!map[tag.id]) map[tag.id] = []
          map[tag.id].push(group)
        }
      }
      return map
    },
    // custom groups sorted by creation time
    sortedCustomGroups () {
      return [...this.customGroups].sort(
        (a, b) => {
          const timeA = new Date(a.createdAt).getTime() || 0
          const timeB = new Date(b.createdAt).getTime() || 0
          return timeA - timeB
        },
      )
    },
    // displayGroup key → sort index (dynamic)
    displayGroupOrder () {
      const order = {}
      let idx = 0
      for (const group of this.sortedCustomGroups) {
        order[`mtag:${group.id}`] = idx++
      }
      order._custom = idx++
      for (const key of Object.keys(uiGroupTagTypeName)) {
        order[key] = idx++
      }
      order._other = idx++
      return order
    },
    // displayGroup key → display label
    displayGroupNames () {
      const names = { ...uiGroupTagTypeName_ }
      for (const group of this.customGroups) {
        names[`mtag:${group.id}`] = group.name
      }
      return names
    },
    groupOptions () {
      const customGroupEntries = this.sortedCustomGroups.map(g => ({ value: `mtag:${g.id}`, label: g.name }))
      const ungroupedCustom = [{ value: '_custom', label: '自定義' }]
      if (!this.includeSystem) return [...customGroupEntries, ...ungroupedCustom]
      const systemGroupEntries = Object.entries(uiGroupTagTypeName).map(([value, label]) => ({ value, label }))
      const other = [{ value: '_other', label: '其他' }]
      return [...customGroupEntries, ...ungroupedCustom, ...systemGroupEntries, ...other]
    },
    sortTags () {
      const order = this.displayGroupOrder
      const tagGroupsMap = this.tagToCustomGroups
      const isSystem = (group) => group !== '_custom' && !group.startsWith('mtag:')
      return [...this.options]
        .filter(tag => this.includeSystem || !isSystem(tag.uiGroup))
        .flatMap(tag => {
          const groups = tagGroupsMap[tag.id] || []
          const entries = []
          for (const group of groups) {
            entries.push({ ...tag, displayGroup: `mtag:${group.id}` })
          }
          entries.push({ ...tag, displayGroup: tag.uiGroup })
          return entries
        })
        .sort((a, b) => {
          const orderA = order[a.displayGroup] ?? Infinity
          const orderB = order[b.displayGroup] ?? Infinity
          return orderA - orderB
        })
    },
    filterTags () {
      let result = this.sortTags
      if (this.excludedTagIds.length) {
        const excludedSet = new Set(this.excludedTagIds)
        result = result.filter(tag => !excludedSet.has(tag.id))
      }
      if (this.nameSearch) {
        result = result.filter(tag => tag.name.includes(this.nameSearch))
      }
      if (this.groupSearch) {
        result = result.filter(tag => tag.displayGroup === this.groupSearch)
      }
      return result
    },
    mixedGroupLabelTags () {
      const temp = [...this.filterTags]
      let groupRecord = {}
      let pageCount = 0
      return temp.reduce((data, tag) => {
        if (tag.displayGroup in groupRecord) {
          data.push({ type: 'option', option: tag })
          pageCount += 1
        } else {
          groupRecord[tag.displayGroup] = '1'
          data.push({ type: 'label', label: this.displayGroupNames[tag.displayGroup] || tag.displayGroup })
          pageCount += 1

          if (pageCount === this.tableOptions.pageLimit) {
            groupRecord = { [tag.displayGroup]: '1' }
            data.push({ type: 'label', label: this.displayGroupNames[tag.displayGroup] || tag.displayGroup })
            pageCount = 1
          }
          data.push({ type: 'option', option: tag })
          pageCount += 1
        }

        if (pageCount === this.tableOptions.pageLimit) {
          pageCount = 0
          groupRecord = {}
        }

        return data
      }, [])
    },
    showTagList () {
      return chunk(this.mixedGroupLabelTags, this.tableOptions.pageLimit)
    },
    selectableTagIds () {
      const ids = new Set(
        this.filterTags
          .filter(tag => !this.disabledTag(tag))
          .map(tag => tag.id),
      )
      return [...ids]
    },
    isAllSelected () {
      if (!this.selectableTagIds.length) return false
      return this.selectableTagIds.every(id => this.selected.includes(id))
    },
    isIndeterminate () {
      if (this.isAllSelected) return false
      return this.selectableTagIds.some(id => this.selected.includes(id))
    },
    // max = 1 為單選情境，「全選」永遠只會勾到一個標籤，不該顯示
    showSelectAll () {
      return this.max !== 1
    },
  },
  methods: {
    toggleSelectAll (checked) {
      if (checked) {
        const selectedSet = new Set(this.selected)
        for (const id of this.selectableTagIds) {
          if (this.max && selectedSet.size >= this.max) break
          selectedSet.add(id)
        }
        this.selected = [...selectedSet]
      } else {
        const selectableSet = new Set(this.selectableTagIds)
        this.selected = this.selected.filter(id => !selectableSet.has(id))
      }
    },
    onClosed () {
      this.selected = []
      this.nameSearch = ''
      this.groupSearch = ''
      this.toggleCreate = false
      this.resetForm()
    },
    onOpen () {
      this.selected = this.modelValue
      this.fetchCustomGroups()
    },
    onSubmit () {
      this.$emit('update:modelValue', this.selected)
      this.observeVisible = false
    },
    removeSelect (id) {
      this.selected = this.selected.filter(tag => tag !== id)
    },
    disabledTag (tag) {
      if (this.disableGroupedTags && this.tagToCustomGroups[tag.id]?.length) return true
      if (this.max) {
        if (this.selected.length >= this.max) {
          if (this.selected.includes(tag.id)) return false
          return true
        }
      }
      return false
    },
    findSelectTag (id) {
      const exist = find(this.options, { id })
      const name = get(exist, 'name')
      return name
    },
  },
})
</script>

<style lang="postcss" scoped>
.wrapper {
  @apply border border-gray-40 rounded-[4px];
}

.selected-wrapper {
  @apply flex flex-wrap max-h-[117px] overflow-y-auto p-[8px] border-b border-gray-40 gap-[8px] min-h-[45px];
}
.selected-tag {
  @apply max-w-[160px] truncate inline-block align-bottom;
}

.list-wrapper {
  @apply p-[8px] h-[372px];
}
.select-all-wrapper {
  @apply pb-[8px] border-b border-gray-40 mb-[8px];
}
.item-group {
  @apply grid grid-flow-col gap-3;
  grid-template-rows: repeat(8, minmax(0, 1fr));
  > * {
    line-height: 14px;
  }
}
:deep(.el-checkbox__input){
  @apply align-text-top;
}

.btn {
  @apply underline text-normal font-normal flex-shrink-0 cursor-pointer;
}
.confirm {
  @apply text-action;
}

.cancel {
  @apply text-danger;
}

.create-tag-toggle {
  @apply mb-[22px] py-[6px];
}

.btn-toggle-create {
  @apply text-[#767676] border-[#636363];
  @apply text-[12px] font-medium leading-[18px];
  @apply px-2 py-1;
  &:hover {
    @apply text-[#555] border-[#555] bg-white;
  }
}

.pagination {
  @apply py-[30px];
}

:deep(.create-tag.el-input){
  @apply w-full;
}

:deep(.el-input) {
  @apply w-full;
}
</style>
